import {
  Grid,
  Typography,
  TextField,
  Button,
  CircularProgress,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { useState } from "react";
import { useAuthContext } from "../../providers/AuthProvider";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import * as XLSX from "xlsx";
import { createAdvertisingOptions } from "../../clients/createAdvertisingOptions";
import CustomSnackbar from "../snackbar/CustomSnackbar";

const AdOptionWithSingleImageUpload = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [parsedData, setParsedData] = useState<any[]>([]); // Store parsed Excel data
  const [columns, setColumns] = useState<string[]>([]); // Store the column headers
  const [adOptionImage, setAdOptionImage] = useState<string>(""); // Store the selected digital medium
  const [isParsed, setIsParsed] = useState(false); // New state for tracking parse status
  const { authHeader } = useAuthContext();
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Function to parse Excel file and extract data
  const parseExcelFile = async (file: File) => {
    return new Promise<any[]>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        const binaryString = event.target?.result;
        const workbook = XLSX.read(binaryString, { type: "binary" });

        // Convert Excel sheet data to JSON format
        const sheetName = workbook.SheetNames[0]; // Get the first sheet name
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet); // Convert to JSON

        resolve(jsonData); // Return parsed JSON data
        setIsParsed(true);
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsBinaryString(file); // Read the file as binary
    });
  };

  // Handle file change
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const uploadedFile = event.target.files?.[0] || null;

    if (uploadedFile) {
      setIsLoading(true);
      try {
        const excelData = await parseExcelFile(uploadedFile);
        const mappedColumns = Object.keys(excelData[0]); // Map the column headers
        setParsedData(excelData);
        setColumns(mappedColumns);
        setIsParsed(true); // Mark as parsed after successfully setting data
      } catch (error) {
        console.error("Error parsing Excel:", error);
        alert("Failed to parse the Excel file.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSnackbar = (
    severity: "success" | "error" | "warning" | "info",
    message: string
  ) => {
    setSnackbarSeverity(severity);
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const sendDataToAPI = async (row: any) => {
    try {
      if (!authHeader) return;
  
      // Embed the image URL inside the data payload
      const formattedData = {
        ...row,
        imageUrls: adOptionImage // Include the image URL inside the JSON
      };
  
      const formData = new FormData();
      formData.append("data", JSON.stringify(formattedData)); // Only one append — no separate imageUrls
  
      const response = await createAdvertisingOptions(formattedData, authHeader);
      console.log("Row successfully sent to API:", response.data);
      handleSnackbar("success", "Row uploaded successfully");
    } catch (error) {
      console.error("Error sending row to API:", error);
      handleSnackbar("error", "Error uploading row");
    }
  };
  

  // Function to upload all rows one by one
  const uploadRowsToAPI = async (data: any[]) => {
    for (const row of data) {
      await sendDataToAPI(row);
    }
  };

  const handleSendData = async () => {
    await uploadRowsToAPI(parsedData);
  };

  return (
    <>
      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
      <Grid
        container
        spacing={2}
        justifyContent="center"
        alignItems="center"
        direction="row"
        xs={12}
      >
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom textAlign={"center"}>
            Ad Option With Image Upload
          </Typography>
        </Grid>

        <Grid item>
          <TextField
            type="file"
            inputProps={{ accept: ".xlsx, .xls" }}
            onChange={handleFileChange}
            variant="outlined"
            fullWidth
            hidden
          />
        </Grid>
        <Grid item>
          <TextField
            label="Image URL"
            variant="outlined"
            value={adOptionImage}
            onChange={(e) => setAdOptionImage(e.target.value)}
            fullWidth
          />
        </Grid>

        <Grid item>
          <Button
            variant="contained"
            color="primary"
            endIcon={<ArrowForwardIcon />}
            onClick={handleSendData}
            disabled={!isParsed || isLoading}
            style={{ marginLeft: "16px" }}
          >
            {isLoading ? <CircularProgress size={24} /> : "Send Data"}
          </Button>
        </Grid>

        {parsedData.length > 0 && columns.length > 0 && (
          <TableContainer
            sx={{ overflow: "auto", px: 2, mt: 8 }}
            component={Paper}
          >
            <Table>
              <TableHead>
                <TableRow>
                  {columns.map((column, index) => (
                    <TableCell
                      key={index}
                      style={{
                        backgroundColor: "#1976D2",
                      }}
                    >
                      {column}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {parsedData.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {columns.map((column, colIndex) => (
                      <TableCell key={colIndex}>
                        {row[column] || "N/A"}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Grid>
    </>
  );
};

export default AdOptionWithSingleImageUpload;
